import {
  makeStyles,
  mergeClasses,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import React, { useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { NoDataCard } from "../../components/common/no-data-card/NoDataCard";
import { LayoutContext } from "../../context/LayoutContext";
import { useApiResource } from "../../hooks/useApiResource";
import { IFlightDetails } from "../../models/flights/IFlightsCommon";
import { getApiUrl } from "../../utils/common/CommonHelperFunctions";
import { transformFlightData } from "../../utils/flights/SingleFlightDashboardHelper";
import SingleFlightMap from "../osm-maps/map-displays/SingleFlightMap";
import BaggageCard from "./BaggageCard";
import DelayCard from "./DelayCard";
import FlightDetailsCard from "./FlightDetailsCard";
import PassengersCard from "./PassengersCard";
import ResourcesCard from "./ResourcesCard";
import TimingMilestonesCard from "./TimingMilestonesCard";

interface ISingleFlightOverviewProps {
  airportCode: string;
}

const SingleFlightOverview: React.FC<ISingleFlightOverviewProps> = () => {
  const styles = useStyles();

  const [params] = useSearchParams();
  const docId = params.get("docId") ?? "";
  const airportCode = params.get("airport") ?? "";

  const { timezone } = useContext(LayoutContext);

  const getFlightDetailsUrl = getApiUrl(
    process.env.REACT_APP_API_FLIGHT_LIST_DETAIL,
    airportCode,
    new Date(),
    new Date(),
    timezone
  ).replace("{flightId}", docId);

  const {
    data: flightDetails,
    isError,
    isFetching,
    isLoading,
  } = useApiResource<IFlightDetails>({
    url: getFlightDetailsUrl,
    refetchOnWindowFocus: false,
    queryKey: ["single-flight-detail", airportCode, timezone],
    refetchInterval: 30000,
  });

  const transformedData = React.useMemo(
    () => transformFlightData(flightDetails),
    [flightDetails]
  );

  const isLiveFlight =
    flightDetails?.liveTracked &&
    flightDetails?.operationalStatus !== "CANCELLED";

  const nodataFlightCard = (
    <div
      className={mergeClasses(
        styles.containerHeight,
        "amd-container-vflex--center",
        "amd-container-vcenter"
      )}
    >
      <NoDataCard message={"No flight found"} />
    </div>
  );

  return (
    <>
      {isLoading && (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className={styles.containerHeight}
        />
      )}
      {isError && nodataFlightCard}
      {!isError && !isFetching && !flightDetails && nodataFlightCard}
      {!isError && !isLoading && flightDetails && (
        <section className={styles.mainContainer}>
          <FlightDetailsCard
            className={mergeClasses(styles.box, styles.box1)}
            data={transformedData.flightDetails}
            airport={airportCode}
          />
          <TimingMilestonesCard
            className={mergeClasses(styles.box, styles.box2)}
            data={transformedData.timing}
          />
          <ResourcesCard
            className={mergeClasses(styles.box, styles.box3)}
            data={transformedData.resources}
          />
          <SingleFlightMap
            className={mergeClasses(styles.box, styles.box4)}
            flightmarkerDetails={transformedData.flightMarker}
            isLive={isLiveFlight || false}
          />
          <PassengersCard
            className={mergeClasses(styles.box, styles.box5)}
            data={transformedData.passengers}
          />
          <BaggageCard
            className={mergeClasses(styles.box, styles.box6)}
            data={transformedData.baggage}
          />
          <DelayCard
            className={mergeClasses(styles.box, styles.box7)}
            data={transformedData.delay}
          />
        </section>
      )}
    </>
  );
};

export default SingleFlightOverview;

const useStyles = makeStyles({
  mainContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gridTemplateRows: "180px 350px 160px",
    gap: "16px",
  },
  box: {
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "24px",
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow4,
    overflow: "hidden",
  },
  box1: { gridColumn: "1 / 3" },
  box2: { gridColumn: "1", gridRow: "2" },
  box3: { gridColumn: "2", gridRow: "2" },
  box4: { gridColumn: "3 / 5", gridRow: "1 / 3", borderRadius: "8px" },
  box5: { gridColumn: "1", gridRow: "3" },
  box6: { gridColumn: "2", gridRow: "3" },
  box7: { gridColumn: "3 / 5", gridRow: "3" },
  containerHeight: {
    minHeight: "712px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
  },
});
